import React, { useEffect, useState } from 'react';
import { Save, TrendingUp, TrendingDown, ShieldCheck, Percent, Plane, Smartphone } from 'lucide-react';
import { api } from '../../api/client';

type SpreadType = 'percentage' | 'flat';

interface SpreadConfig {
  type: SpreadType;
  value: number;
}

interface SpreadSettings {
  buy: SpreadConfig;
  sell: SpreadConfig;
}

type CreditProtectPlanType = 'basic' | 'standard' | 'premium';

interface CreditProtectTier {
  planType: CreditProtectPlanType;
  monthlyPremium: number;
  coverageAmount: number;
}

type CreditProtectPolicyType = 'travel' | 'device';

interface CreditProtectPolicyPricing {
  policyType: CreditProtectPolicyType;
  premium: number;
  coverageAmount: number;
}

interface CreditProtectTierSettings {
  tiers: CreditProtectTier[];
  annualDiscountPercent: number;
  policyPricing: CreditProtectPolicyPricing[];
}

/**
 * Admin-facing TBAUOP buy/sell spread editor. The backend stores percentage
 * spreads as a fraction (0.005 = 0.5%) — this page converts to/from a
 * human-friendly "0.5" percent input so admins don't have to think in
 * fractions; flat spreads are a plain USD amount, no conversion needed.
 */
function SpreadEditor({
  label,
  icon,
  spread,
  onSave,
}: {
  label: string;
  icon: React.ReactNode;
  spread: SpreadConfig;
  onSave: (type: SpreadType, value: number) => Promise<void>;
}) {
  const [type, setType] = useState<SpreadType>(spread.type);
  const [displayValue, setDisplayValue] = useState(() => toDisplayValue(spread));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null);

  // Re-sync if the parent reloads fresh data (e.g. after the other editor saves).
  useEffect(() => {
    setType(spread.type);
    setDisplayValue(toDisplayValue(spread));
  }, [spread]);

  function toDisplayValue(s: SpreadConfig): string {
    return s.type === 'percentage' ? String(s.value * 100) : String(s.value);
  }

  const handleSave = async () => {
    const parsed = Number(displayValue);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setMessage({ text: 'Enter a non-negative number.', isError: true });
      return;
    }
    if (type === 'percentage' && parsed > 100) {
      setMessage({ text: 'Percentage spread must be 100 or less.', isError: true });
      return;
    }

    const backendValue = type === 'percentage' ? parsed / 100 : parsed;
    setSaving(true);
    setMessage(null);
    try {
      await onSave(type, backendValue);
      setMessage({ text: 'Saved.', isError: false });
    } catch (e: any) {
      setMessage({ text: e?.response?.data?.message || 'Could not save.', isError: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface1 rounded-xl p-6 border border-border">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center">{icon}</div>
        <h3 className="text-textPrimary font-semibold">{label}</h3>
      </div>

      <div className="flex gap-2 mb-4">
        {(['percentage', 'flat'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setType(t)}
            className={`text-sm px-3 py-1.5 rounded-lg border transition ${
              type === t ? 'bg-gold/10 border-gold/40 text-gold' : 'bg-surface2 border-border text-textSecondary hover:border-gold/30'
            }`}
          >
            {t === 'percentage' ? 'Percentage' : 'Flat ($)'}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <input
            type="number"
            min={0}
            step="0.01"
            value={displayValue}
            onChange={(e) => setDisplayValue(e.target.value)}
            className="w-full px-3 py-2 bg-surface2 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
          />
          <span className="absolute right-3 top-2 text-textTertiary text-sm pointer-events-none">
            {type === 'percentage' ? '%' : 'USD'}
          </span>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-gold text-bg font-medium hover:bg-gold/90 transition disabled:opacity-40"
        >
          <Save size={14} /> {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      <p className="text-textTertiary text-xs mt-2">
        {type === 'percentage'
          ? 'Applied as a percentage of the live gold spot price.'
          : 'Applied as a fixed dollar amount added to (buy) or subtracted from (sell) the spot price.'}
      </p>

      {message && (
        <p className={`text-xs mt-2 ${message.isError ? 'text-error' : 'text-success'}`}>{message.text}</p>
      )}
    </div>
  );
}

/** One tier's premium + coverage, editable and saved independently of the others. */
function TierEditor({
  tier,
  onSave,
}: {
  tier: CreditProtectTier;
  onSave: (
    planType: CreditProtectPlanType,
    monthlyPremium: number,
    coverageAmount: number,
  ) => Promise<void>;
}) {
  const [monthlyPremium, setMonthlyPremium] = useState(String(tier.monthlyPremium));
  const [coverageAmount, setCoverageAmount] = useState(String(tier.coverageAmount));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null);

  useEffect(() => {
    setMonthlyPremium(String(tier.monthlyPremium));
    setCoverageAmount(String(tier.coverageAmount));
  }, [tier]);

  const handleSave = async () => {
    const premium = Number(monthlyPremium);
    const coverage = Number(coverageAmount);
    if (!Number.isFinite(premium) || premium < 0) {
      setMessage({ text: 'Enter a non-negative premium.', isError: true });
      return;
    }
    if (!Number.isFinite(coverage) || coverage <= 0) {
      setMessage({ text: 'Enter a positive coverage amount.', isError: true });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await onSave(tier.planType, premium, coverage);
      setMessage({ text: 'Saved.', isError: false });
    } catch (e: any) {
      setMessage({ text: e?.response?.data?.message || 'Could not save.', isError: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface1 rounded-xl p-6 border border-border">
      <h3 className="text-textPrimary font-semibold capitalize mb-4">{tier.planType}</h3>

      <label className="text-textTertiary text-xs">Monthly Premium (USD)</label>
      <div className="relative mb-3">
        <input
          type="number"
          min={0}
          step="0.01"
          value={monthlyPremium}
          onChange={(e) => setMonthlyPremium(e.target.value)}
          className="w-full px-3 py-2 bg-surface2 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
        />
      </div>

      <label className="text-textTertiary text-xs">Coverage Amount (USD)</label>
      <div className="relative mb-4">
        <input
          type="number"
          min={0}
          step="0.01"
          value={coverageAmount}
          onChange={(e) => setCoverageAmount(e.target.value)}
          className="w-full px-3 py-2 bg-surface2 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
        />
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-gold text-bg font-medium hover:bg-gold/90 transition disabled:opacity-40"
      >
        <Save size={14} /> {saving ? 'Saving...' : 'Save'}
      </button>

      {message && (
        <p className={`text-xs mt-2 ${message.isError ? 'text-error' : 'text-success'}`}>{message.text}</p>
      )}
    </div>
  );
}

/** The discount applied when a user pays for a year upfront instead of monthly. */
function DiscountEditor({
  annualDiscountPercent,
  onSave,
}: {
  annualDiscountPercent: number;
  onSave: (annualDiscountPercent: number) => Promise<void>;
}) {
  const [displayValue, setDisplayValue] = useState(String(annualDiscountPercent * 100));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null);

  useEffect(() => {
    setDisplayValue(String(annualDiscountPercent * 100));
  }, [annualDiscountPercent]);

  const handleSave = async () => {
    const percent = Number(displayValue);
    if (!Number.isFinite(percent) || percent < 0 || percent >= 100) {
      setMessage({ text: 'Enter a percentage between 0 and 100 (exclusive).', isError: true });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await onSave(percent / 100);
      setMessage({ text: 'Saved.', isError: false });
    } catch (e: any) {
      setMessage({ text: e?.response?.data?.message || 'Could not save.', isError: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface1 rounded-xl p-6 border border-border">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center">
          <Percent className="text-gold" size={16} />
        </div>
        <h3 className="text-textPrimary font-semibold">Annual Billing Discount</h3>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <input
            type="number"
            min={0}
            max={99}
            step="0.1"
            value={displayValue}
            onChange={(e) => setDisplayValue(e.target.value)}
            className="w-full px-3 py-2 bg-surface2 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
          />
          <span className="absolute right-3 top-2 text-textTertiary text-sm pointer-events-none">%</span>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-gold text-bg font-medium hover:bg-gold/90 transition disabled:opacity-40"
        >
          <Save size={14} /> {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      <p className="text-textTertiary text-xs mt-2">
        Applied to 12 months of premium when a user chooses annual billing instead of monthly.
      </p>

      {message && (
        <p className={`text-xs mt-2 ${message.isError ? 'text-error' : 'text-success'}`}>{message.text}</p>
      )}
    </div>
  );
}

const POLICY_ICONS: Record<CreditProtectPolicyType, React.ReactNode> = {
  travel: <Plane className="text-gold" size={16} />,
  device: <Smartphone className="text-gold" size={16} />,
};

/** One travel/device policy's premium + coverage — self-funded benefit pricing, not real insurance. */
function PolicyPricingEditor({
  pricing,
  onSave,
}: {
  pricing: CreditProtectPolicyPricing;
  onSave: (
    policyType: CreditProtectPolicyType,
    premium: number,
    coverageAmount: number,
  ) => Promise<void>;
}) {
  const [premium, setPremium] = useState(String(pricing.premium));
  const [coverageAmount, setCoverageAmount] = useState(String(pricing.coverageAmount));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null);

  useEffect(() => {
    setPremium(String(pricing.premium));
    setCoverageAmount(String(pricing.coverageAmount));
  }, [pricing]);

  const handleSave = async () => {
    const p = Number(premium);
    const c = Number(coverageAmount);
    if (!Number.isFinite(p) || p < 0) {
      setMessage({ text: 'Enter a non-negative premium.', isError: true });
      return;
    }
    if (!Number.isFinite(c) || c <= 0) {
      setMessage({ text: 'Enter a positive coverage amount.', isError: true });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await onSave(pricing.policyType, p, c);
      setMessage({ text: 'Saved.', isError: false });
    } catch (e: any) {
      setMessage({ text: e?.response?.data?.message || 'Could not save.', isError: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface1 rounded-xl p-6 border border-border">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center">
          {POLICY_ICONS[pricing.policyType]}
        </div>
        <h3 className="text-textPrimary font-semibold capitalize">{pricing.policyType}</h3>
      </div>

      <label className="text-textTertiary text-xs">Premium (USD)</label>
      <div className="relative mb-3">
        <input
          type="number"
          min={0}
          step="0.01"
          value={premium}
          onChange={(e) => setPremium(e.target.value)}
          className="w-full px-3 py-2 bg-surface2 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
        />
      </div>

      <label className="text-textTertiary text-xs">Coverage Amount (USD)</label>
      <div className="relative mb-4">
        <input
          type="number"
          min={0}
          step="0.01"
          value={coverageAmount}
          onChange={(e) => setCoverageAmount(e.target.value)}
          className="w-full px-3 py-2 bg-surface2 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
        />
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-gold text-bg font-medium hover:bg-gold/90 transition disabled:opacity-40"
      >
        <Save size={14} /> {saving ? 'Saving...' : 'Save'}
      </button>

      {message && (
        <p className={`text-xs mt-2 ${message.isError ? 'text-error' : 'text-success'}`}>{message.text}</p>
      )}
    </div>
  );
}

export default function AdminSettings() {
  const [settings, setSettings] = useState<SpreadSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Independent from the spread state above: a failure loading one section
  // must not blank out the other, since they are unrelated settings.
  const [creditProtect, setCreditProtect] = useState<CreditProtectTierSettings | null>(null);
  const [creditProtectLoading, setCreditProtectLoading] = useState(true);
  const [creditProtectError, setCreditProtectError] = useState('');

  const load = () => {
    setLoading(true);
    api
      .get<SpreadSettings>('/admin/settings/spread')
      .then((res) => setSettings(res.data))
      .catch(() => setError('Failed to load spread settings'))
      .finally(() => setLoading(false));
  };

  const loadCreditProtect = () => {
    setCreditProtectLoading(true);
    api
      .get<CreditProtectTierSettings>('/admin/settings/credit-protect-tiers')
      .then((res) => setCreditProtect(res.data))
      .catch(() => setCreditProtectError('Failed to load Credit Protect settings'))
      .finally(() => setCreditProtectLoading(false));
  };

  useEffect(() => {
    load();
    loadCreditProtect();
  }, []);

  const saveBuy = async (type: SpreadType, value: number) => {
    const res = await api.patch<SpreadConfig>('/admin/settings/spread/buy', { type, value });
    setSettings((prev) => (prev ? { ...prev, buy: res.data } : prev));
  };

  const saveSell = async (type: SpreadType, value: number) => {
    const res = await api.patch<SpreadConfig>('/admin/settings/spread/sell', { type, value });
    setSettings((prev) => (prev ? { ...prev, sell: res.data } : prev));
  };

  const saveTier = async (
    planType: CreditProtectPlanType,
    monthlyPremium: number,
    coverageAmount: number,
  ) => {
    const res = await api.patch<CreditProtectTier>(
      `/admin/settings/credit-protect-tiers/${planType}`,
      { monthlyPremium, coverageAmount },
    );
    setCreditProtect((prev) =>
      prev
        ? {
            ...prev,
            tiers: prev.tiers.map((t) => (t.planType === planType ? res.data : t)),
          }
        : prev,
    );
  };

  const saveDiscount = async (annualDiscountPercent: number) => {
    const res = await api.patch<{ annualDiscountPercent: number }>(
      '/admin/settings/credit-protect-tiers/discount',
      { annualDiscountPercent },
    );
    setCreditProtect((prev) =>
      prev ? { ...prev, annualDiscountPercent: res.data.annualDiscountPercent } : prev,
    );
  };

  const savePolicyPricing = async (
    policyType: CreditProtectPolicyType,
    premium: number,
    coverageAmount: number,
  ) => {
    const res = await api.patch<CreditProtectPolicyPricing>(
      `/admin/settings/credit-protect-tiers/policy/${policyType}`,
      { premium, coverageAmount },
    );
    setCreditProtect((prev) =>
      prev
        ? {
            ...prev,
            policyPricing: prev.policyPricing.map((p) =>
              p.policyType === policyType ? res.data : p,
            ),
          }
        : prev,
    );
  };

  if (loading) return <div className="p-8 text-textSecondary">Loading...</div>;
  if (error || !settings) return <div className="p-8 text-error">{error || 'No data'}</div>;

  return (
    <div className="p-8">
      <h1 className="font-display text-2xl font-bold text-textPrimary mb-2">Settings</h1>
      <p className="text-textSecondary text-sm mb-6">
        Configure the Gold buy/sell spread applied on top of the live gold spot price.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
        <SpreadEditor
          label="Buy Spread"
          icon={<TrendingUp className="text-gold" size={16} />}
          spread={settings.buy}
          onSave={saveBuy}
        />
        <SpreadEditor
          label="Sell Spread"
          icon={<TrendingDown className="text-gold" size={16} />}
          spread={settings.sell}
          onSave={saveSell}
        />
      </div>

      <div className="flex items-center gap-2 mt-10 mb-2">
        <ShieldCheck className="text-gold" size={18} />
        <h2 className="font-display text-lg font-semibold text-textPrimary">
          Credit Protect Coverage Tiers
        </h2>
      </div>
      <p className="text-textSecondary text-sm mb-6">
        Premium and coverage per tier, and the discount applied when a user pays annually. New
        enrollments and the next billing cycle use these values; already-billed cycles don't
        change retroactively.
      </p>

      {creditProtectLoading && <div className="text-textSecondary text-sm mb-6">Loading...</div>}
      {!creditProtectLoading && (creditProtectError || !creditProtect) && (
        <div className="text-error text-sm mb-6">{creditProtectError || 'No data'}</div>
      )}
      {!creditProtectLoading && creditProtect && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl">
          {creditProtect.tiers.map((tier) => (
            <TierEditor key={tier.planType} tier={tier} onSave={saveTier} />
          ))}
          <DiscountEditor
            annualDiscountPercent={creditProtect.annualDiscountPercent}
            onSave={saveDiscount}
          />
        </div>
      )}

      {!creditProtectLoading && creditProtect && (
        <>
          <div className="flex items-center gap-2 mt-10 mb-2">
            <ShieldCheck className="text-gold" size={18} />
            <h2 className="font-display text-lg font-semibold text-textPrimary">
              Travel &amp; Device Coverage
            </h2>
          </div>
          <p className="text-textSecondary text-sm mb-6">
            Premium and coverage for the self-funded travel/device benefit — paid out of
            Bankably's own funds the same way overdraft cover is, not underwritten insurance.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl">
            {creditProtect.policyPricing.map((pricing) => (
              <PolicyPricingEditor
                key={pricing.policyType}
                pricing={pricing}
                onSave={savePolicyPricing}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
